import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  Output,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AngularMaterialModule } from 'src/app/module/angular-material/angular-material.module';
import { CompanyState } from 'src/app/shared/state/company.state';
import { GoogleMapConfig } from '../../config/google-map.config';
import { AutoCompleteAddressComponent, Coordinate } from '../../interface/google-map.interface';
import { GoogleAutocompleteComponent } from '../google-autocomplete/google-autocomplete.component';

@Component({
  selector: 'app-location-picker',
  standalone: true,
  imports: [AngularMaterialModule, GoogleAutocompleteComponent],
  templateUrl: './location-picker.component.html',
  styleUrl: './location-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationPickerComponent implements AfterViewInit, OnDestroy {
  /**
   * Google maps instance
   */
  private _map!: google.maps.Map;

  public center = signal<Coordinate>({ lat: 39.793807, lng: -101.359829 });
  public mapId: string = Math.round(Math.random() * 10000).toString();

  @Output() coordinate: EventEmitter<Coordinate> = new EventEmitter<Coordinate>();

  constructor(
    private companyState: CompanyState,
    public _dialogRef: MatDialogRef<LocationPickerComponent, Coordinate | null>,
    @Inject(MAT_DIALOG_DATA) private dialogData: { center?: Coordinate }
  ) {}

  ngOnDestroy(): void {
    if (this._map) {
      google.maps.event.clearInstanceListeners(this._map);
    }
  }

  ngAfterViewInit(): void {
    if (this.dialogData.center?.lat && this.dialogData.center?.lng) {
      const lat = Number(this.dialogData.center?.lat).toFixed(6);
      const lng = Number(this.dialogData.center?.lng).toFixed(6);

      this.center.set({ lat, lng });
    }

    this._initGoogleMap();
  }

  public onAddressChange(event: AutoCompleteAddressComponent): void {
    if (event.latitude && event.longitude) {
      this.center.set({ lat: event.latitude, lng: event.longitude });
      this._map.setZoom(20);
      this._map.setCenter(
        new google.maps.LatLng(parseFloat(this.center()?.lat?.toString()), parseFloat(this.center()?.lng?.toString()))
      );
    }
  }

  /**
   * Initialize the Google map and add a location market to the center of the map.
   */
  private async _initGoogleMap() {
    this._map = new google.maps.Map(document.getElementById(this.mapId) as HTMLElement, {
      mapId: this.companyState.companySetting.googleMapId,
      center: new google.maps.LatLng(
        parseFloat(this.center()?.lat?.toString()),
        parseFloat(this.center()?.lng?.toString())
      ),
      zoom: this.dialogData.center ? 17 : 4,
      gestureHandling: 'greedy',
      clickableIcons: false,
      streetViewControl: false,
      zoomControl: false,
      controlSize: 30,
      mapTypeControl: false,
      fullscreenControl: false,
      draggableCursor: 'default',
      restriction: {
        latLngBounds: GoogleMapConfig.restrictedBounds,
      },
      minZoom: 3,
      maxZoom: 20,
    });

    await new Promise<void>((resolve) => {
      google.maps.event.addListenerOnce(this._map, 'idle', () => {
        return resolve();
      });
    });

    this._map.setOptions({
      gestureHandling: 'greedy',
      zoomControl: true,
      cameraControl: false,
      mapTypeControl: true,
      fullscreenControl: false,
      streetViewControl: false,
    });

    google.maps.event.addListener(this._map, 'center_changed', () => {
      const center = this._map.getCenter();
      if (!center) return;
      const lat = Number(center.lat().toFixed(6));
      const lng = Number(center.lng().toFixed(6));
      this.center.set({ lat, lng });
    });
  }
}
